import {
  AreaTooltip,
  BaseChartDataItem,
  Box,
  ArrowRed,
  calculatePercentageChange,
  Disclaimer,
  Div,
  formatAmount,
  formatDateByGroupByTypeChart,
  GroupByType,
  H3,
  H5,
  Icon,
  Img,
  lightTheme,
  ModalPortal,
  NumberType,
  P,
  Pill,
  Separator,
  renderPercentageChange,
  Tab,
  TooltipIcon,
  TooltipWrapper,
  useIsTruncated,
} from '@vgrubs/components';
import { useEffect, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  currencyYAxisWidth,
  CurrencyYAxisTick,
  CustomXAxisTick,
} from './chartHelpers';

export type TabKey = 'graph' | 'breakdown';

// Up to 5 legend rows on the card; the rest live behind a "View all" modal.
// Rows are two lines tall (label over amount + share), so a longer list would
// outgrow the pie next to it and stretch the card past its siblings.
// Cards with a short, fixed service list raise this via `breakdownLegendLimit`
// so every slice stays on the card - there the rows add up to the headline,
// and hiding one behind "View all" makes the card look like it does not.
const LEGEND_LIMIT = 5;

/** A supporting figure listed under a drill-in line - what the line's amount
 *  is made of, or a second amount that belongs to it but is not part of it. */
export type BreakdownSubDetail = {
  label: string;
  /** Amount in cents. A figure the row has no value for is left out of the
   *  list entirely rather than printed as "$0.00" or a dash. */
  amount: number;
};

/** A small colored chip after a drill-in line's name, for a ratio that
 *  belongs to the row but is NOT its share of the parent - that share already
 *  sits under the amount. Read against some other figure of the row's own
 *  (e.g. its amount over a different total), which the caller works out. */
export type BreakdownSubTag = {
  label: string;
  /** Text color. */
  color: string;
  background: string;
  /** Hover explanation of what the ratio is and which two figures it comes
   *  from - the chip itself has no room to say. Goes through the app tooltip
   *  (not a native `title`), so it looks like every other tooltip here and
   *  sits above the modal. */
  tooltip?: string;
};

/** One line of a legend row's drill-in (e.g. a provider under vGrubs VR). */
export type BreakdownSubItem = {
  label: string;
  /** Amount in cents. May be negative - a drill-in line is a real slice of the
   *  parent, and a slice can go into the red. */
  amount: number;
  /** Secondary text under the label, e.g. "412 orders". */
  note?: string;
  /** Chip rendered right after the label. */
  tag?: BreakdownSubTag;
  /** Row image (a provider logo). Already resolved to a src by the caller, so
   *  this file stays free of any provider vocabulary. */
  icon?: string;
  /** Small label/amount lines under the row. Deliberately outside the share
   *  column: they are not slices of the parent, so giving them a percentage
   *  would invite adding them up with the row above. */
  details?: BreakdownSubDetail[];
};

/** What one legend row splits into, shown behind an arrow on that row. */
export type BreakdownDrilldown = {
  /** Heading above the list in the modal, e.g. "By Provider". */
  title: string;
  rows: BreakdownSubItem[];
};

export type BreakdownItem = {
  label: string;
  /** Amount in cents (positive magnitude). */
  amount: number;
  /** Optional slice color override; falls back to PIE_COLORS by index. */
  color?: string;
  /** Optional info icon next to the legend label, for a line whose name does
   *  not explain what it is on its own. */
  tooltip?: string;
  /** What this row is made of. When set, the row gets an arrow that opens the
   *  split in a modal - deliberately not inline, which would grow the card and
   *  every card stretched alongside it. These lines are NOT pie slices either:
   *  the parent keeps its single slice, so their shares are read against the
   *  parent amount, not the card total. */
  drilldown?: BreakdownDrilldown;
  /** For a row whose details are fetched on their own rather than carried
   *  here (Monthly Saas): the same arrow, but the click is handed to the
   *  caller, which opens its own screen. Wins over `drilldown`. */
  onOpen?: () => void;
};

const PIE_COLORS = [
  lightTheme.primary,
  lightTheme.darkGray,
  lightTheme.green,
  lightTheme.darkBlue,
  lightTheme.blue,
];

/**
 * Collapse entries that carry the same name into one slice and one legend row.
 *
 * Expense names are free text with no uniqueness rule, so the live list holds
 * "Sentry" twice and "Tech Salaries" twice. Two rows spelled identically read
 * as one line to anyone looking at the pie, and splitting the money across
 * them only makes the ranking wrong. Matching is on the trimmed label,
 * case-insensitively, so a stray space or capital cannot split what looks the
 * same on screen; the first occurrence keeps its spelling and its color.
 *
 * Deliberately NOT applied in the Custom Expenses manager: every definition
 * stays its own row there, because that is where they get edited and deleted.
 *
 * Only the first entry's `drilldown` survives a merge, which is fine while the
 * two never meet: duplicate labels come from free-text expense names, and rows
 * that drill down come from fixed service lists. Merging the splits too would
 * need them to be summable by label, which they are not in general.
 */
const mergeSameLabel = <T extends BreakdownItem>(list: T[]): T[] => {
  const byName = new Map<string, T>();

  list.forEach((item) => {
    const label = item.label.trim();
    const groupKey = label.toLowerCase();
    const seen = byName.get(groupKey);
    // Spread first, then add into the copy - the caller's objects stay untouched.
    if (seen) seen.amount += item.amount;
    else byName.set(groupKey, { ...item, label });
  });

  return [...byName.values()];
};

// ─── Segmented toggle (uses the shared design-system `Tab`) ─────────────────
export const TabToggle = ({
  value,
  onChange,
}: {
  value: TabKey;
  onChange: (v: TabKey) => void;
}) => {
  const tabs: { key: TabKey; label: string }[] = [
    { key: 'breakdown', label: 'Pie' },
    { key: 'graph', label: 'Graph' },
  ];

  return (
    <Div $flex $alignCenter $gap="8px">
      {tabs.map((t) => (
        <Tab
          key={t.key}
          $small
          selected={t.key === value}
          onClick={() => onChange(t.key)}
          // Inline style wins over the design-system's media-queried
          // min-width rules, which otherwise clamp the pill to 150px on
          // desktop / 100px on mobile regardless of $minWidthAuto*.
          style={{ minWidth: 'auto', width: '80px' }}
        >
          {t.label}
        </Tab>
      ))}
    </Div>
  );
};

// ─── Trend body: area chart, compare-aware ──────────────────────────────────
export const TrendBody = ({
  data,
  seriesKey,
  seriesColor,
  tooltipLabel,
  negateTooltip,
  groupByType,
  isComparing,
}: {
  data: BaseChartDataItem[];
  seriesKey: string;
  seriesColor: string;
  tooltipLabel: string;
  negateTooltip: boolean;
  groupByType: GroupByType;
  isComparing: boolean;
}) => {
  const compareKey = `${seriesKey}ToCompare`;

  const TrendTooltip = ({
    payload,
    label,
  }: {
    payload?: { dataKey: string; value: number }[];
    label?: string;
  }) => {
    if (!payload?.length) return null;
    const valueFor = (k: string) => payload.find((p) => p.dataKey === k)?.value;
    const flip = (n: number | undefined) =>
      n === undefined ? undefined : negateTooltip ? -n : n;
    const current = flip(valueFor(seriesKey));
    const compare = flip(valueFor(compareKey));

    return (
      <AreaTooltip>
        <P $m="0" $bold>
          {formatDateByGroupByTypeChart(label ?? '', groupByType, true)}
        </P>
        {current !== undefined && (
          <Div $flex $alignCenter $between $gap="12px">
            <P $m="0">{tooltipLabel}</P>
            <P $m="0" $color={seriesColor} $bold>
              {formatAmount(current, NumberType.USD)}
            </P>
          </Div>
        )}
        {compare !== undefined && (
          <Div $flex $alignCenter $between $gap="12px">
            <Disclaimer $m="0">Previous</Disclaimer>
            <Disclaimer $m="0" $color={lightTheme.darkGray} $bold>
              {formatAmount(compare, NumberType.USD)}
            </Disclaimer>
          </Div>
        )}
      </AreaTooltip>
    );
  };

  return (
    <Div $width="100%" $height="280px" $heightSM="275px">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="2 2" />
          <XAxis
            dataKey="key"
            tick={(props) => (
              <CustomXAxisTick {...props} groupByType={groupByType} />
            )}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={CurrencyYAxisTick}
            // Same reason as the P&L card: a wide "K" label would otherwise be
            // drawn past the left edge of the card on mobile.
            width={currencyYAxisWidth(
              data.flatMap((d) =>
                [d[seriesKey], d[compareKey]].filter(
                  (v): v is number => typeof v === 'number'
                )
              )
            )}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            cursor={false}
            wrapperStyle={{ outline: 'none' }}
            content={(p) => <TrendTooltip {...(p as any)} />}
          />
          <Area
            type="monotone"
            dataKey={seriesKey}
            stroke="none"
            fill={seriesColor}
            fillOpacity={0.8}
          />
          {isComparing && (
            <Area
              type="monotone"
              dataKey={compareKey}
              stroke={lightTheme.darkGray}
              strokeDasharray="5 5"
              fill="transparent"
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </Div>
  );
};

// Legend label pinned to one line. When the text does not fit it ellipsizes,
// and the shared app tooltip shows the full label on hover - the tooltip only
// exists while the label is actually cut off.
const TruncatedLegendLabel = ({ label }: { label: string }) => {
  const ref = useRef<HTMLParagraphElement | null>(null);
  const truncated = useIsTruncated(ref, label);

  return (
    <TooltipWrapper tooltip={truncated && label} style={{ minWidth: 0 }}>
      <P $m="0" $treeDots ref={ref}>
        {label}
      </P>
    </TooltipWrapper>
  );
};

// ─── Breakdown body: pie + legend ────────────────────────────────────────────
const BreakdownBody = ({
  items: itemsIn,
  amountLabel,
  amountColor,
  negateAmounts,
  ranked,
  legendLimit,
  title,
  periodLabel,
}: {
  items: BreakdownItem[];
  amountLabel: string;
  amountColor: string;
  negateAmounts: boolean;
  /** Order the rows biggest-first. */
  ranked: boolean;
  /** How many legend rows fit on the card before "View all" takes over. */
  legendLimit: number;
  /** Card title — reused as the "View all" modal heading. */
  title: string;
  /** Active date range, passed to the modal as its subtitle. The full list is
   *  what gets screenshotted out of the app, and a list of totals with no
   *  window on it cannot be checked against a monthly run rate. */
  periodLabel?: string;
}) => {
  type Item = BreakdownItem & {
    percentage: string;
    color: string;
    key: string;
  };
  const total = itemsIn.reduce((acc, it) => acc + it.amount, 0);
  // Colors resolve against the INCOMING order, before the merge and the ranked
  // sort below, so neither can reshuffle the slices that don't set one.
  const colored = itemsIn.map((it, i) => ({
    ...it,
    color: it.color ?? PIE_COLORS[i % PIE_COLORS.length],
  }));
  // Percentages come after the merge so a collapsed row reads as one share
  // rather than two that have to be added up by eye.
  const prepared: Item[] = mergeSameLabel(colored).map((it, i) => ({
    ...it,
    percentage: total > 0 ? ((it.amount / total) * 100).toFixed(2) : '0.00',
    // The merge already makes labels unique, so this is belt and braces: the
    // index is taken before the ranked sort, so a row keeps its key when the
    // order changes. Keying on the label alone used to let React collapse
    // repeats and silently drop money from the list.
    key: `${i}-${it.label}`,
  }));
  // Biggest contributor first: the card legend is truncated to the top N, so
  // the slices that actually move the headline are the ones kept visible.
  const items: Item[] = ranked
    ? [...prepared].sort((a, b) => b.amount - a.amount)
    : prepared;

  const visibleItems = items.slice(0, legendLimit);
  const hasMore = items.length > legendLimit;

  const [modalOpen, setModalOpen] = useState(false);
  // The row whose split is open. Held as the item itself so the modal can show
  // its label, its total and its share denominator without looking it up again.
  const [drilldownItem, setDrilldownItem] = useState<Item | null>(null);

  const PieTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const item = payload[0]?.payload as Item;
    return (
      <AreaTooltip>
        <P $m="0" $bold>
          {item.label}
        </P>
        <P $m="0">
          {amountLabel}:{' '}
          <span style={{ color: amountColor }}>
            {formatAmount(
              negateAmounts ? -item.amount : item.amount,
              NumberType.USD
            )}
          </span>
        </P>
        <Disclaimer $m="0">{item.percentage}% of total</Disclaimer>
      </AreaTooltip>
    );
  };

  // Pie always renders every slice — only the legend list is truncated.
  const renderPie = () => (
    <Div $width="220px" $widthSM="100%" $height="220px" $heightSM="150px">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={items}
            cx="50%"
            cy="50%"
            innerRadius={0}
            outerRadius="100%"
            dataKey="amount"
            nameKey="label"
            labelLine={false}
            animationDuration={1000}
          >
            {items.map((item, i) => (
              <Cell key={`cell-${i}`} fill={item.color} />
            ))}
          </Pie>
          <Tooltip content={<PieTooltip />} />
        </PieChart>
      </ResponsiveContainer>
    </Div>
  );

  // Label over amount on the left, share on the right, centered against the
  // two of them. Cramming all three onto one line is what forced the long
  // expense names into an ellipsis while the numbers still fought for room.
  //
  // `onCard` = rendered in the card's legend rather than in the "View all"
  // modal. Card rows keep the label on one line (ellipsis + tooltip when it
  // does not fit); the modal, where the full text lives, wraps instead.
  const renderLegendRows = (rows: Item[], onCard: boolean) =>
    rows.map((item) => {
      const openRow = item.onOpen
        ? item.onOpen
        : (item.drilldown?.rows.length ?? 0) > 0
        ? () => setDrilldownItem(item)
        : undefined;
      const canDrillDown = openRow != null;

      return (
        <Div
          key={item.key}
          $flex
          $alignCenter
          $between
          $gap="12px"
          $mb="8"
          $mbSM="8"
          $pointer={canDrillDown}
          onClick={openRow}
        >
          <Div style={{ minWidth: 0 }}>
            <Div $flex $alignCenter $gap="8px" style={{ minWidth: 0 }}>
              <span
                style={{
                  flex: 'none',
                  display: 'inline-block',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: item.color,
                }}
              />
              {onCard ? (
                <TruncatedLegendLabel label={item.label} />
              ) : (
                <P $m="0" $wordBreak="break-word">
                  {item.label}
                </P>
              )}
              {item.tooltip && (
                // flex:none so the icon never gets squeezed out by a long label
                // - the label is the part that is allowed to ellipsize.
                <Div $flex $alignCenter style={{ flex: 'none' }}>
                  <TooltipIcon tooltip={item.tooltip} />
                </Div>
              )}
              {canDrillDown && (
                // Sits with the label rather than out at the right edge: the
                // share column is right-aligned across every row, and an arrow
                // on one row only would step that column in for that row.
                <Icon
                  $size="14"
                  color={lightTheme.lightGray}
                  style={{ flex: 'none' }}
                >
                  <ArrowRed />
                </Icon>
              )}
            </Div>
            {/* Amount sits under the label, indented past the color dot.
                Tabular digits keep the column aligned down the list. */}
            <Disclaimer
              $m="0"
              $color={lightTheme.lightGray}
              style={{
                paddingLeft: '18px',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatAmount(
                negateAmounts ? -item.amount : item.amount,
                NumberType.USD
              )}
            </Disclaimer>
          </Div>
          <Disclaimer
            $m="0"
            $right
            $color={lightTheme.darkGray}
            style={{
              flex: 'none',
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {item.percentage}%
          </Disclaimer>
        </Div>
      );
    });

  // One legend row's split, in its own modal. Same row shape as the per-
  // location "By Service" drilldown next door, so the two read alike.
  const renderDrilldownModal = () => {
    const parent = drilldownItem;

    return (
      <ModalPortal
        isOpen={parent != null}
        closeModal={() => setDrilldownItem(null)}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="560px"
        maxHeight="80vh"
        modalOverflow="auto"
        titleWithExit={parent?.label ?? ''}
        subtitle={periodLabel}
        // Above the "View all" modal, which is where this can be opened from.
        zIndex={135}
      >
        {parent && (
          <>
            <H5 $mb="8">{parent.drilldown?.title}</H5>
            {(parent.drilldown?.rows ?? []).map((row, i) => {
              const amount = negateAmounts ? -row.amount : row.amount;
              // Share of the PARENT, not of the card total - the line is a
              // split of the row it was opened from. A parent of 0 can still
              // carry lines (money booked on one side of the range only), so
              // the guard is not dead code.
              const share =
                parent.amount > 0 ? (row.amount / parent.amount) * 100 : 0;
              // A slice can go negative (an order that lost money). Red is the
              // only signal that the minus is not a formatting accident.
              const negative = amount < 0;

              return (
                <Div key={row.label}>
                  <Div
                    $flex
                    $between
                    $alignCenter
                    $minHeight="50px"
                    $gap="12px"
                  >
                    <Div
                      $flex
                      $alignCenter
                      $gap="16px"
                      $gapSM="12px"
                      style={{ minWidth: 0 }}
                    >
                      {row.icon ? (
                        <Img
                          alt=""
                          src={row.icon}
                          $width="40px"
                          $widthSM="36px"
                          style={{ flex: 'none' }}
                        />
                      ) : (
                        // No logo for this provider. An initial in a neutral
                        // disc keeps the column aligned without borrowing some
                        // other brand's mark for it.
                        <Div
                          $flex
                          $center
                          $width="40px"
                          $widthSM="36px"
                          $height="40px"
                          $heightSM="36px"
                          style={{
                            flex: 'none',
                            borderRadius: '50%',
                            backgroundColor: lightTheme.gray,
                            color: lightTheme.darkGray,
                            fontWeight: 700,
                          }}
                        >
                          {row.label.charAt(0).toUpperCase()}
                        </Div>
                      )}
                      <Div style={{ minWidth: 0 }}>
                        <Div $flex $alignCenter $gap="8px" $wrap>
                          <H5 $m="0" $left $wordBreak="break-word">
                            {i + 1}. {row.label}
                          </H5>
                          {row.tag && (
                            // The wrapper's inline-block div would otherwise
                            // add line-height space under the chip and push
                            // the row taller than its neighbours.
                            <TooltipWrapper
                              tooltip={row.tag.tooltip}
                              style={{ flex: 'none', lineHeight: 0 }}
                            >
                              <Pill
                                $background={row.tag.background}
                                $minWidth="0"
                                $minWidthSM="0"
                                $height="20px"
                                $padding="0 8px"
                              >
                                <Disclaimer
                                  $m="0"
                                  $bold
                                  $noWrap
                                  $color={row.tag.color}
                                  style={{
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {row.tag.label}
                                </Disclaimer>
                              </Pill>
                            </TooltipWrapper>
                          )}
                        </Div>
                        {row.note && (
                          <P $m="0" $color={lightTheme.lightGray}>
                            {row.note}
                          </P>
                        )}
                      </Div>
                    </Div>

                    <Div $flex $column style={{ flex: 'none' }}>
                      <H5
                        $m="0"
                        $right
                        $color={
                          negative ? lightTheme.primary : lightTheme.green
                        }
                        style={{
                          whiteSpace: 'nowrap',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatAmount(amount, NumberType.USD)}
                      </H5>
                      <P
                        $m="0"
                        $right
                        $color={lightTheme.lightGray}
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {share.toFixed(2)}%
                      </P>
                    </Div>
                  </Div>

                  {/* Indented past the logo so the lines read as belonging to
                      the row above rather than as rows of their own. */}
                  {!!row.details?.length && (
                    <Div $p="0 0 8px 56px" $pSM="0 0 8px 48px">
                      {row.details.map((detail) => {
                        const value = negateAmounts
                          ? -detail.amount
                          : detail.amount;

                        return (
                          <Div
                            key={detail.label}
                            $flex
                            $between
                            $alignCenter
                            $gap="12px"
                          >
                            <P $m="0" $color={lightTheme.lightGray}>
                              {detail.label}
                            </P>
                            <P
                              $m="0"
                              $right
                              $color={
                                value < 0
                                  ? lightTheme.primary
                                  : lightTheme.darkGray
                              }
                              style={{
                                flex: 'none',
                                whiteSpace: 'nowrap',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {formatAmount(value, NumberType.USD)}
                            </P>
                          </Div>
                        );
                      })}
                    </Div>
                  )}

                  <Separator $m="8px 0" $color={lightTheme.lightGray} />
                </Div>
              );
            })}

            <H5 $right $m="0" $mt="10">
              Total:
            </H5>
            <H3 $right $mb="0" $color={lightTheme.green}>
              {formatAmount(
                negateAmounts ? -parent.amount : parent.amount,
                NumberType.USD
              )}
            </H3>
          </>
        )}
      </ModalPortal>
    );
  };

  return (
    <>
      <Div
        $width="100%"
        // Min- rather than fixed height: legend rows are single-line, but a
        // fixed 280px would let an unexpectedly tall list spill outside the
        // card instead of growing it.
        $minHeight="280px"
        $minHeightSM="auto"
        $heightSM="auto"
        $flex
        $alignCenter
        $justifyEvenly
        $columnSM
        $p="0 20px 16px 20px"
        $gapSM="8px"
      >
        {renderPie()}
        <Div $width="50%" $widthSM="100%" $mt="24" $mtSM="0">
          {renderLegendRows(visibleItems, true)}
          {hasMore && (
            <Disclaimer
              $m="8px 0 0 0"
              $mSM="4px 0 0 0"
              $bold
              $center
              $pointer
              $color={lightTheme.primary}
              onClick={() => setModalOpen(true)}
            >
              View all
            </Disclaimer>
          )}
        </Div>
      </Div>

      <ModalPortal
        isOpen={modalOpen}
        closeModal={() => setModalOpen(false)}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="600px"
        maxHeight="80vh"
        modalOverflow="auto"
        titleWithExit={title}
        subtitle={periodLabel}
      >
        <Div $flex $alignStart $justifyEvenly $columnSM $gap="16px">
          {renderPie()}
          {/* The list carries its own scroll instead of riding the modal's, so
              the pie stays in view the whole way down a long breakdown.
              `position: sticky` cannot do this here: ModalPortal's content
              wrapper is overflow:hidden, which makes it the sticky element's
              scrollport, and that box never scrolls. The max() floor keeps the
              list usable on a short window, where the subtraction alone would
              collapse it. */}
          <Div
            $width="50%"
            $widthSM="100%"
            $maxHeight="max(220px, calc(80vh - 170px))"
            $maxHeightSM="max(180px, calc(80vh - 300px))"
            $overflow="auto"
            // Right padding keeps the share column clear of the scrollbar,
            // which on macOS is drawn as an overlay on top of the content.
            $p="0 14px 0 0"
          >
            {renderLegendRows(items, false)}
          </Div>
        </Div>
      </ModalPortal>

      {renderDrilldownModal()}
    </>
  );
};

/**
 * Generic finance card with a Pie / Graph toggle under a single shared
 * headline — used by Net Revenue (Total Income) and Operating Expenses.
 *
 * Breakdown data only exists on the single-period overview (the /compare
 * endpoint omits it), so when comparison turns on we snap to the Graph view;
 * the user can still flip back manually.
 */
export const FinanceTabbedCard = ({
  title,
  breakdownTitle,
  headlineCents,
  headlineCompareCents,
  negateDisplay = false,
  valueColor,
  series,
  seriesCompared,
  seriesKey,
  tooltipLabel,
  breakdownItems,
  breakdownAmountLabel,
  breakdownRanked = false,
  breakdownLegendLimit = LEGEND_LIMIT,
  periodLabel,
  groupByType,
  isComparing,
  headerExtra,
}: {
  title: string;
  /** Optional card title override while the Pie tab is active. */
  breakdownTitle?: string;
  /** Headline total in cents (positive magnitude). */
  headlineCents: number;
  headlineCompareCents: number;
  /** Display money with a flipped sign (expenses → "-$X.XX"). */
  negateDisplay?: boolean;
  valueColor: string;
  series: BaseChartDataItem[];
  seriesCompared: BaseChartDataItem[];
  /** dataKey in `series`; compare key is `${seriesKey}ToCompare`. */
  seriesKey: string;
  tooltipLabel: string;
  breakdownItems: BreakdownItem[];
  /** Label in the pie tooltip ("Income" / "Expenses"). */
  breakdownAmountLabel: string;
  /** Order the legend rows biggest-first. Operating Expenses only: the other
   *  cards have a short, hand-ordered list where the fixed order is the point.
   *  (The amount itself shows on every card's rows, ranked or not.) */
  breakdownRanked?: boolean;
  /** Legend rows kept on the card before the rest move behind "View all".
   *  Raise it for a short fixed list whose rows are meant to add up to the
   *  headline. */
  breakdownLegendLimit?: number;
  /** Active date range ("Jan 01, 2025 - Aug 25, 2026"). Shown under the
   *  headline so the totals carry their window wherever the card ends up. */
  periodLabel?: string;
  groupByType: GroupByType;
  isComparing: boolean;
  /** Optional node rendered to the right of the tab toggle (e.g. an arrow
   *  that opens a side-panel modal). */
  headerExtra?: React.ReactNode;
}) => {
  const [tab, setTab] = useState<TabKey>('breakdown');
  useEffect(() => {
    if (isComparing) setTab('graph');
  }, [isComparing]);

  const display = (cents: number) =>
    formatAmount(negateDisplay ? -cents : cents, NumberType.USD);

  // Compare pill — same renderer ReusableChart uses. For expense-like cards
  // (negateDisplay) a rising value is bad, so the colors invert.
  const calculatedPercentage = calculatePercentageChange(
    headlineCents || 0,
    headlineCompareCents || 0
  );

  return (
    <Box $p="0" $pSM="0" $relative>
      {/* headerExtra (e.g. the custom-expenses arrow). On mobile the header
          stacks into a column, so pin it to the card's top-right corner next
          to the title; on desktop it rides inline with the tabs (below). */}
      {headerExtra && (
        <Div
          $mobile
          $absolute
          $flex
          $alignCenter
          style={{ top: 16, right: 20, zIndex: 1 }}
        >
          {headerExtra}
        </Div>
      )}
      {/* Shared header */}
      <Div
        $flex
        $alignCenter
        $between
        $gap="12px"
        $p="16px 20px 0 20px"
        $columnSM
        $alignStartSM
      >
        <Div>
          <Div $flex $alignCenter $gap="8px">
            <H5 $m="0">{title}</H5>
            {isComparing &&
              tab === 'graph' &&
              renderPercentageChange(calculatedPercentage, negateDisplay)}
          </Div>
          <Div $flex $alignCenter $gap="8px">
            <H3 $mb="0" $color={valueColor}>
              {display(headlineCents)}
            </H3>
            {isComparing && (
              <>
                <Disclaimer $m="0" $color={lightTheme.lightGray}>
                  vs {display(headlineCompareCents)}
                </Disclaimer>
              </>
            )}
          </Div>
          {periodLabel && (
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              {periodLabel}
            </Disclaimer>
          )}
        </Div>
        <Div $flex $alignCenter $gap="8px">
          <TabToggle value={tab} onChange={setTab} />
          {/* Desktop: arrow sits inline with the tabs so it's vertically
              centered with them. Hidden on mobile (rendered absolutely above). */}
          {headerExtra && (
            <Div $desktop $flex $alignCenter>
              {headerExtra}
            </Div>
          )}
        </Div>
      </Div>

      {/* Body */}
      {tab === 'graph' ? (
        <TrendBody
          data={isComparing ? seriesCompared : series}
          seriesKey={seriesKey}
          seriesColor={valueColor}
          tooltipLabel={tooltipLabel}
          negateTooltip={negateDisplay}
          groupByType={groupByType}
          isComparing={isComparing}
        />
      ) : (
        <BreakdownBody
          items={breakdownItems}
          amountLabel={breakdownAmountLabel}
          amountColor={negateDisplay ? lightTheme.primary : lightTheme.green}
          negateAmounts={negateDisplay}
          ranked={breakdownRanked}
          legendLimit={breakdownLegendLimit}
          title={title}
          periodLabel={periodLabel}
        />
      )}
    </Box>
  );
};
